import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { PeriodTabs } from '../components/PeriodTabs';
import { RaporTablosu } from '../components/rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../components/rapor-ekrani/RaporHatasi';
import { FILTRE_GIRIS, FiltreAlani, RaporFiltreBari, gecerliTarih } from '../components/rapor-ekrani/RaporFiltreBari';
import { useDoluYukseklik } from '../components/rapor-ekrani/useDoluYukseklik';
import { useCekmeceSecimi } from '../components/rapor-ekrani/useCekmeceSecimi';
import { ekranKaynagi, useRaporSorgu } from '../components/rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../components/rapor-ekrani/useDarEkran';
import { tarihMetni } from '../components/rapor-ekrani/bicim';
import type { RaporKolonu } from '../components/rapor-ekrani/kolon';
import { AracSatisCekmecesi, MOBIL_NOTU } from '../components/arac-satis/AracSatisCekmecesi';
import {
  MALIYET_TURU_SECENEKLERI, aracSatiri, hepsiSifir, karYuzdesi, kari, maliyeti, tahsilatOrani, toplamKarYuzdesi,
  type AracSatiri, type HamAracSatiri, type MaliyetTuru,
} from '../components/arac-satis/tipler';
import type { EkranProps } from '../ekranKayitDefteri';

const SAYI = new Intl.NumberFormat('tr-TR');
/** Varsayılan dönem: bugün dahil son 30 gün. */
const VARSAYILAN_GUN = 30;
const TUTAR_NOTU = 'tutarlar KDV dahil · kâr % maliyete oranla';

/**
 * S350: Araç Satış ve Kârlılık (ERP12 rapor ekranları; onaylı demo `tasks/s334/erp12-rapor-demosu.html`, `arac`).
 * Liste `liste` ds'inden (`@BASTARIH`, `@BITTARIH`, `@MOBIL`; satır = araç / lokasyon), satıra basınca açılan
 * çekmece `urunler` ds'inden beslenir; kaynaklar `firm_panel_screens.config.kaynaklar` sözlüğünden çözülür.
 *
 * "Maliyet türü" İSTEMCİDE seçer: üç maliyet kolonu satırda hazırdır, Maliyet / Kâr / Kâr % seçili türle
 * hesaplanır (sorgu yinelenmez). Dönem ya da "Yalnız mobil satışlar" değişince liste yeniden sorgulanır ve
 * önceki liste o arada ekranda kalır; mobil işaretliyken `@MOBIL = '1'` gider, değilken parametre gönderilmez.
 * Çekmecedeki araç URL'de durur (`?arac=<lokasyon_id>`).
 *
 * İade, İskonto ve Bedelsiz kolonlarından listedeki BÜTÜN satırlarda 0 olanlar çizilmez (13 kolon 1440'ta
 * sığmıyor). Demodaki "Geciken" ve "Toplam Kalan" kolonları bu sürümde yok.
 *
 * TELEFON (<= 767 px): liste KART (üstte araç adı, sağında net satış; altında maliyet, kâr, kâr %,
 * tahsilat). Yapışık barda yalnız maliyet türü seçicisi; ötekiler katlanır "Filtreler"in içinde.
 */
export default function AracSatisRaporu({ screen }: EkranProps) {
  const listeKaynagi = ekranKaynagi(screen, 'liste');
  const urunlerKaynagi = ekranKaynagi(screen, 'urunler');
  const telefon = useDarEkran();
  const doluRef = useDoluYukseklik<HTMLDivElement>(telefon ? 'enAz' : 'yukseklik');

  const [maliyetTuru, setMaliyetTuru] = useState<MaliyetTuru>('fifoSon');
  const [mobil, setMobil] = useState(false);
  // Tarihler iki durumdur: `...Giris` alanda yazan, öteki sorguya giden (elle yazarken ara değerler gitmez).
  const [varsayilan] = useState(() => {
    const bugun = dayjs();
    return { bas: bugun.subtract(VARSAYILAN_GUN - 1, 'day').format('YYYY-MM-DD'), bit: bugun.format('YYYY-MM-DD') };
  });
  const [bas, setBas] = useState(varsayilan.bas);
  const [bit, setBit] = useState(varsayilan.bit);
  const [basGiris, setBasGiris] = useState(bas);
  const [bitGiris, setBitGiris] = useState(bit);
  const ters = bas > bit;

  const q = useRaporSorgu<HamAracSatiri>(listeKaynagi, { '@BASTARIH': bas, '@BITTARIH': bit, '@MOBIL': mobil ? '1' : '' }, !ters);
  const araclar = useMemo(() => (q.data ?? []).map(aracSatiri), [q.data]);

  const kolonlar = useMemo<RaporKolonu<AracSatiri>[]>(() => {
    const maliyet = (s: AracSatiri) => maliyeti(s, maliyetTuru);
    const kar = (s: AracSatiri) => kari(s, maliyetTuru);
    const tumu: RaporKolonu<AracSatiri>[] = [
      { id: 'arac', baslik: 'Araç / Lokasyon', tur: 'metin', deger: (s) => s.ad, esnek: true, kart: 'baslik' },
      { id: 'satis', baslik: 'Satış', tur: 'para', deger: (s) => s.satis, toplam: true, kart: 'gizli' },
      { id: 'iade', baslik: 'İade', tur: 'para', deger: (s) => s.iade, toplam: true, kart: 'gizli' },
      { id: 'net_satis', baslik: 'Net Satış', tur: 'para', deger: (s) => s.net, toplam: true, kart: 'olcu' },
      { id: 'iskonto', baslik: 'İskonto', tur: 'para', deger: (s) => s.iskonto, toplam: true, kart: 'gizli' },
      { id: 'bedelsiz', baslik: 'Bedelsiz', tur: 'para', deger: (s) => s.bedelsiz, toplam: true, kart: 'gizli' },
      { id: 'maliyet', baslik: 'Maliyet', tur: 'para', deger: maliyet, toplam: true, kart: 'ikincil' },
      { id: 'kar', baslik: 'Kâr', tur: 'para', deger: kar, toplam: true, kart: 'ikincil' },
      {
        id: 'kar_yuzde', baslik: 'Kâr %', tur: 'yuzde', deger: (s) => karYuzdesi(kar(s), maliyet(s)), kart: 'ikincil',
        // Toplamda oran, satır oranlarının toplamı değil TOPLAMLARA uygulanan aynı kuraldır.
        toplam: (satirlar) => toplamKarYuzdesi(satirlar, maliyetTuru),
      },
      { id: 'yedi_gun_satis', baslik: 'Son 7 Gün Net Satış', tur: 'para', deger: (s) => s.yediGun, toplam: true, kart: 'gizli' },
      { id: 'ay_sonu_net_ciro', baslik: 'Ay Sonu Net Ciro', tur: 'para', deger: (s) => s.aySonu, toplam: true, kart: 'gizli' },
      { id: 'tahsilat', baslik: 'Tahsilat', tur: 'para', deger: (s) => s.tahsilat, toplam: true, kart: 'ikincil' },
      {
        id: 'tahsilat_orani', baslik: 'Tahsilat / Satış', tur: 'yuzde', deger: (s) => tahsilatOrani(s.tahsilat, s.net), kart: 'gizli',
        toplam: (satirlar) => tahsilatOrani(satirlar.reduce((t, s) => t + s.tahsilat, 0), satirlar.reduce((t, s) => t + s.net, 0)),
      },
    ];
    // Tablo 1440'ta sığsın: hiçbir araçta değeri olmayan İade / İskonto / Bedelsiz kolonu çizilmez.
    const bos = new Set<string>();
    if (hepsiSifir(araclar, (s) => s.iade)) bos.add('iade');
    if (hepsiSifir(araclar, (s) => s.iskonto)) bos.add('iskonto');
    if (hepsiSifir(araclar, (s) => s.bedelsiz)) bos.add('bedelsiz');
    return tumu.filter((k) => !bos.has(k.id));
  }, [araclar, maliyetTuru]);

  const secim = useCekmeceSecimi('arac');
  const seciliArac = useMemo(
    () => (secim.secili ? araclar.find((s) => s.id === secim.secili) : undefined),
    [araclar, secim.secili],
  );
  const { ac } = secim;
  const satirTiklandi = useCallback((s: AracSatiri) => ac(s.id), [ac]);

  const ilkYukleme = q.isLoading;
  const yenileniyor = q.isFetching && !ilkYukleme;

  const tarihAlani = (etiket: string, giris: string, setGiris: (v: string) => void, uygula: (v: string) => void, gecerli: string) => (
    <FiltreAlani etiket={etiket}>
      <input
        type="date"
        className={FILTRE_GIRIS}
        value={giris}
        onChange={(e) => {
          setGiris(e.target.value);
          if (gecerliTarih(e.target.value)) uygula(e.target.value);
        }}
        onBlur={() => { if (!gecerliTarih(giris)) setGiris(gecerli); }}
      />
    </FiltreAlani>
  );

  const donem = `${tarihMetni(bas)} - ${tarihMetni(bit)}`;
  const etkinAdlar = [
    (bas !== varsayilan.bas || bit !== varsayilan.bit) && 'Dönem',
    mobil && 'Kapsam',
  ].filter((x): x is string => !!x);
  const bilgiYok = ilkYukleme || !listeKaynagi || ters;

  return (
    <div
      ref={doluRef}
      data-testid="arac-satis-raporu"
      className={telefon ? 'flex flex-col gap-2 -mb-4 rk-kenar-16' : 'flex flex-col gap-2 min-h-[320px]'}
    >
      <RaporFiltreBari
        testid="arac-satis-filtre-bar"
        birincil={
          <FiltreAlani etiket="Maliyet türü" grup>
            <PeriodTabs value={maliyetTuru} onChange={setMaliyetTuru} secenekler={MALIYET_TURU_SECENEKLERI} kompakt />
          </FiltreAlani>
        }
        etkinAdlar={etkinAdlar}
        ozet={bilgiYok ? undefined : yenileniyor ? 'Yenileniyor…' : `${SAYI.format(araclar.length)} araç / lokasyon · ${donem}`}
      >
        {tarihAlani('Başlangıç', basGiris, setBasGiris, setBas, bas)}
        {tarihAlani('Bitiş', bitGiris, setBitGiris, setBit, bit)}
        <FiltreAlani etiket="Kapsam" grup>
          <label className="inline-flex items-center gap-2 h-8 max-md:h-11 text-[13px] text-ink-900 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-brand-600" checked={mobil} onChange={(e) => setMobil(e.target.checked)} />
            Yalnız mobil satışlar
          </label>
        </FiltreAlani>
      </RaporFiltreBari>

      {/* Telefonda bilgi satırı kısadır (sayı ve dönem "Filtreler" başlığında); tutar notu burada kalır. */}
      <div className="text-[13px] text-ink-500 min-h-[20px]" data-testid="arac-satis-bilgi">
        {bilgiYok ? (
          <>&nbsp;</>
        ) : telefon ? (
          <>{TUTAR_NOTU}{mobil && <> · {MOBIL_NOTU}</>}</>
        ) : (
          <>
            <b className="font-semibold text-ink-900">{SAYI.format(araclar.length)}</b> araç / lokasyon
            {' · '}{donem} · {TUTAR_NOTU}
            {mobil && <> · {MOBIL_NOTU}</>}
            {yenileniyor && <span className="ml-2 text-ink-400" role="status">Yenileniyor…</span>}
          </>
        )}
      </div>

      {ters ? (
        <RaporHatasi testid="arac-satis-ters" mesaj="Başlangıç tarihi bitiş tarihinden sonra olamaz." />
      ) : !listeKaynagi ? (
        <RaporHatasi testid="arac-satis-kaynaksiz" mesaj="Bu firmada araç satış ve kârlılık raporu kaynağı tanımlı değil." />
      ) : q.isError ? (
        <RaporHatasi testid="arac-satis-hata" mesaj="Araç satış ve kârlılık raporu yüklenemedi." onTekrar={() => q.refetch()} />
      ) : (
        <RaporTablosu
          testid="arac-satis-tablosu"
          etiket="Araç Satış ve Kârlılık"
          kolonlar={kolonlar}
          satirlar={araclar}
          satirKimligi={(s) => s.id}
          onSatirTikla={satirTiklandi}
          seciliKimlik={seciliArac?.id ?? null}
          // Mobil kapsamı satır sayısını azaltmadan da toplamı daraltır (aracın yalnız mobil satışları kalır).
          suzuldu={mobil}
          bosMesaj="Bu dönemde satışı olan araç / lokasyon yok."
          yukleniyor={ilkYukleme}
          yenileniyor={yenileniyor}
        />
      )}

      <AracSatisCekmecesi
        arac={ters ? undefined : seciliArac}
        kaynak={urunlerKaynagi}
        bas={bas}
        bit={bit}
        mobil={mobil}
        maliyetTuru={maliyetTuru}
        onKapat={secim.kapat}
      />
    </div>
  );
}
